'use client';

import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Link } from 'src/navigation';
import SearchBar from 'components/controls/search-bar';
import { ResultCard } from 'components/site-search/result-card';
import styles from 'components/site-search/site-search.module.scss';
import { SITE_SEARCH_PER_PAGE } from 'lib/constants.mjs';
import { routes } from '@metmuseum/routes';
import { clearFocus } from 'helpers/clear-focus';
import {
	trackSiteSearchFacetChanged,
	trackSiteSearchPaginated,
	trackSiteSearchResultClicked,
	trackSiteSearchResultsLoaded,
	trackSiteSearchSubmitted,
} from 'lib/analytics/trackSearch';
import { eventsCalendarRoute } from 'lib/routes';
import { ToggleButton } from 'components/controls/buttons/toggle-button';

const defaultSelectedOption = 'All Results';

const searchAPI = '/api/site_search?';
let abortController = null;

const SearchResultApp = ({
	initQuery,
	initialFacets,
	initialResults,
	inititaltotalResults,
	showHighlights,
}) => {
	const initQueryObj = new URLSearchParams(initQuery);
	initQueryObj.delete('locale');
	const initialSearchParamsString = initQueryObj.toString();
	const [selectedOption, setSelectedOption] = useState(
		initQueryObj.get('searchFacet') || defaultSelectedOption,
	);
	const [searchParamsString, setSearchParamsString] = useState(
		initialSearchParamsString,
	);
	const [totalResults, setTotalResults] = useState(inititaltotalResults);
	const [facet, setFacet] = useState(initialFacets);
	const [results, setResults] = useState(initialResults);
	const [query, setQuery] = useState(initQueryObj.get('q'));

	const [isSearching, setIsSearching] = useState(false);
	const [prevQuery, setPrevQuery] = useState(null);
	const [page, setPage] = useState(1);
	const submittedQuery = new URLSearchParams(searchParamsString).get('q');

	const handleResults = (responseData, page) => {
		if (page === 1) {
			setResults(responseData.results);
			//Facets and Total Results shouldnt change when changing facet or page.
			if (prevQuery === null || prevQuery !== query) {
				//Order Facets by result count
				responseData.facets[0].values.sort((a, b) => b.count - a.count);
				setFacet(responseData.facets[0]);
			}
			setTotalResults(responseData.totalResults);
			setSelectedOption(
				responseData.request.searchFacet || defaultSelectedOption,
			);
		} else {
			//For Pages beyond the first, concat them with previously fetched pages.
			const oldResults = results;
			setResults(oldResults.concat(responseData.results));
		}
	};

	const callAPI = async (paramsString, page = 1) => {
		setIsSearching(true);

		abortController && abortController.abort();
		abortController = new AbortController();
		const request = await fetch(`${searchAPI}${paramsString}&page=${page}`, {
			signal: abortController.signal,
		}); // this Next.js app
		const response = await request.json();

		if (response.results) {
			handleResults(response, page);
			page === 1 &&
				trackSiteSearchResultsLoaded({
					searchQuerystring: paramsString,
					resultsCount: response.totalResults,
				});
			setIsSearching(false);
		} else {
			console.log('No Results');
			setResults([]);
			setTotalResults(0);
			setFacet(response.facet);
			page === 1 &&
				trackSiteSearchResultsLoaded({
					searchQuerystring: paramsString,
					resultsCount: 0,
				});
			setIsSearching(false);
		}
	};

	const searchCollection = async (paramsString, page = 1) => {
		try {
			await callAPI(paramsString, page);
		} catch (e) {
			console.error(e);
		} finally {
			abortController = null;
			setPrevQuery(query);
		}
	};

	const handleSearchQueryChange = (event) => {
		setQuery(event.target.value);
	};

	const handleSearchSubmit = (searchTerm) => {
		const submittedTerm = searchTerm?.trim() || '';
		const paramsObject = new URLSearchParams(searchParamsString);
		const currentTerm = paramsObject.get('q') || '';

		if (submittedTerm === currentTerm) {
			clearFocus();
			return;
		}

		if (submittedTerm) {
			paramsObject.set('q', submittedTerm);
			trackSiteSearchSubmitted({
				searchOrigin: 'Site Search Results',
				searchTerm: submittedTerm,
			});
		} else {
			paramsObject.delete('q');
		}

		setPage(1);
		const nextSearchParamsString = paramsObject.toString();

		setSearchParamsString(nextSearchParamsString);
		searchCollection(nextSearchParamsString);
		clearFocus();
	};

	const handleSearchButtonClick = () => {
		handleSearchSubmit(query);
	};

	const handleSearchKeyDown = (event) => {
		if (event.key === 'Enter') {
			handleSearchSubmit(event.currentTarget.value);
		}
	};

	const handleSelectedOptionChange = (event) => {
		const nextSelectedOption = event.target.value;
		setSelectedOption(nextSelectedOption);
		const paramsObject = new URLSearchParams(searchParamsString);
		paramsObject.set('searchFacet', nextSelectedOption);
		paramsObject.get('searchFacet') === defaultSelectedOption &&
			paramsObject.delete('searchFacet');
		setPage(1);
		const nextSearchParamsString = paramsObject.toString();

		trackSiteSearchFacetChanged({
			searchQuerystring: nextSearchParamsString,
		});

		setSearchParamsString(nextSearchParamsString);
		searchCollection(nextSearchParamsString);
	};

	const handleShowMoreResults = () => {
		const newPage = page + 1;

		trackSiteSearchPaginated({
			searchQuerystring: searchParamsString,
		});

		setPage(newPage);
		searchCollection(searchParamsString, newPage);
	};

	const handleResultClick = (result, index) => {
		trackSiteSearchResultClicked({
			resultRank: index + 1,
			resultTitle: result.title,
			resultType: result.cardType,
			resultUrl: result.url,
			searchQuerystring: searchParamsString,
		});
	};

	useEffect(() => {
		trackSiteSearchResultsLoaded({
			searchQuerystring: initialSearchParamsString,
			resultsCount: inititaltotalResults,
		});

		const onFacetChange = (e) => {
			const newParams = new URLSearchParams(e.target.location.search);
			setSearchParamsString(newParams.toString());
		};
		window.addEventListener('popstate', onFacetChange);
		return () => {
			window.removeEventListener('popstate', onFacetChange);
		};
	}, [initialSearchParamsString, inititaltotalResults]);

	const updateURL = (searchParams) => {
		const params = new URLSearchParams(searchParams);
		if (
			params.get('searchFacet') !==
			new URLSearchParams(window.location.search.slice(1)).get('searchFacet')
		) {
			window.history.pushState({}, '', `${location.pathname}?${params}`);
			const navEvent = new PopStateEvent('popstate');
			window.dispatchEvent(navEvent);
		} else {
			window.history.replaceState(
				window.history.state,
				'',
				`${location.pathname}?${params}`,
			);
		}
	};

	useEffect(() => {
		setPage(1);
		updateURL(searchParamsString);
	}, [searchParamsString]);

	const mainClasses = isSearching
		? `${styles.isSearching} ${styles.generalSearch}`
		: styles.generalSearch;

	return (
		<main className={mainClasses}>
			<h1 className={styles.title}>Search / {selectedOption}</h1>
			<h2 className={styles.subTitle}>
				{submittedQuery && totalResults
					? `${totalResults.toLocaleString('en-US')} results for ${submittedQuery}`
					: ''}
			</h2>
			<section className={styles.search}>
				<SearchBar
					id="site-search-bar"
					debounceTimeout={0}
					isSearching={isSearching}
					initialQuery={query}
					placeholderText={`Search ${selectedOption || ''}`}
					onChange={handleSearchQueryChange}
					onButtonClick={handleSearchButtonClick}
					onKeyDown={handleSearchKeyDown}
				/>
			</section>
			<section className={styles.facets}>
				<div className={styles.facetsWrapper}>
					{facet?.values?.map((value) => {
						if (value.id === '') {
							return;
						}
						return (
							<button
								key={value.id}
								className={styles.facet}
								disabled={selectedOption === value.id}
								value={value.id}
								onClick={handleSelectedOptionChange}
							>
								{value.label} ({value.count.toLocaleString()})
							</button>
						);
					})}
				</div>
			</section>
			<SearchHelperBanner
				selectedOption={selectedOption}
				query={submittedQuery}
			/>
			{results?.length > 0 ? (
				<section className={styles.results}>
					{results.map((result, i) => {
						return (
							<ResultCard
								showHighlights={showHighlights}
								loading={isSearching}
								lazyLoad={i > 1}
								onResultClick={() => handleResultClick(result, i)}
								key={
									result?.key
										? `${i}-${result.key}`
										: `${result.sectionName}-${result.id}-${i}-${isSearching}`
								}
								result={result}
							/>
						);
					})}
				</section>
			) : (
				<section className={styles.noResults}>
					There are no results found. Please try another search.
				</section>
			)}
			<section className={styles.pagination}>
				{totalResults > page * SITE_SEARCH_PER_PAGE && (
					<ToggleButton onClick={handleShowMoreResults} text={'Show more'} />
				)}
			</section>
		</main>
	);
};

SearchResultApp.propTypes = {
	initQuery: PropTypes.object,
	initialFacets: PropTypes.object,
	initialResults: PropTypes.array,
	inititaltotalResults: PropTypes.number,
	showHighlights: PropTypes.bool,
};

const SearchHelperBanner = ({ selectedOption, query }) => {
	if (selectedOption === 'Events' || selectedOption === 'Art') {
		const link =
			selectedOption === 'Events' ? (
				<Link
					title={`Search for events ${query && `related to ${query}`}`}
					href={eventsCalendarRoute}
				>
					Advanced Event Search
				</Link>
			) : (
				<Link
					title={`Search our collection ${query && `for ${query}`}`}
					href={routes.advancedCollectionSearch({ q: query })}
				>
					Advanced Collection Search
				</Link>
			);
		return (
			<section className={styles.notification}>
				Need to refine your search? Use our {link} for more options and filters.
			</section>
		);
	} else if (selectedOption === 'Exhibitions') {
		return (
			<section className={styles.notification}>
				Looking for current exhibitions? Check the{' '}
				<Link href={`/exhibitions`}>Current Exhibitions</Link> page.
			</section>
		);
	} else {
		return <section className={styles.notification} />;
	}
};

SearchHelperBanner.propTypes = {
	query: PropTypes.string,
	selectedOption: PropTypes.string,
};

export default SearchResultApp;
